<script setup>
import { ref, onMounted } from 'vue'
import axios from 'axios'

const props = defineProps({
  leagueId: {
    type: [String, Number],
    required: true
  },
  teamId: {
    type: [String, Number],
    required: true
  }
})

// Component state
const loading = ref(false)
const error = ref(null)
const opponentMatchUps = ref(null)

// Fetch leagues opponent match-ups on mount
const fetchOpponentMatchUps = async () => {
  if (!props.leagueId || !props.teamId) {
    error.value = 'League ID and Team ID are required'
    return
  }

  loading.value = true
  error.value = null

  try {
    const response = await axios.post('api/v2/projections-settings/leagues/opponent-match-ups', {
      league_id: props.leagueId,
      team_id: props.teamId
    })
    
    opponentMatchUps.value = response.data
  } catch (err) {
    error.value = err.response?.data?.message || err.message || 'Failed to fetch opponent match-ups'
    console.error('Error fetching opponent match-ups:', err)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchOpponentMatchUps()
})
</script>

<template>
  <div class="space-y-4">
    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center py-8">
      <div class="flex items-center gap-3">
        <i class="fas fa-spinner fa-spin text-blue-500"></i>
        <span class="text-gray-600">Loading opponent match-ups...</span>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4">
      <div class="flex items-center gap-3">
        <i class="fas fa-exclamation-triangle text-red-500"></i>
        <div>
          <h4 class="text-red-800 font-medium">Error Loading Opponent Match-ups</h4>
          <p class="text-red-600 text-sm mt-1">{{ error }}</p>
        </div>
      </div>
      <button 
        @click="fetchOpponentMatchUps" 
        class="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
      >
        <i class="fas fa-redo mr-2"></i>
        Retry
      </button>
    </div>

    <!-- Success State -->
    <div v-else-if="opponentMatchUps" class="space-y-4">
      <div class="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <div class="flex items-center gap-3">
          <i class="fas fa-users text-orange-500"></i>
          <div>
            <h4 class="text-orange-800 font-medium">Opponent Match-ups Loaded</h4>
            <p class="text-orange-600 text-sm mt-1">
              Found {{ Array.isArray(opponentMatchUps) ? opponentMatchUps.length : Object.keys(opponentMatchUps).length }} opponent match-up entries
            </p>
          </div>
        </div>
      </div>

      <!-- Opponent Match-ups Display -->
      <div class="bg-white rounded-lg border overflow-hidden">
        <div class="p-4 border-b bg-gray-50">
          <h5 class="font-medium text-gray-800">Leagues Opponent Match-ups</h5>
        </div>
        
        <!-- If it's an array -->
        <div v-if="Array.isArray(opponentMatchUps)" class="p-4">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div v-for="(matchup, index) in opponentMatchUps" :key="index" 
                 class="bg-gray-50 rounded-lg p-3 border">
              <div class="text-sm font-medium text-gray-900 mb-2">Match-up {{ index + 1 }}</div>
              <div class="text-xs text-gray-600">
                <pre class="whitespace-pre-wrap">{{ JSON.stringify(matchup, null, 2) }}</pre>
              </div>
            </div>
          </div>
        </div>
        
        <!-- If it's an object -->
        <div v-else class="p-4">
          <div class="space-y-3">
            <div v-for="(value, key) in opponentMatchUps" :key="key" 
                 class="bg-gray-50 rounded-lg p-3 border">
              <div class="text-sm font-medium text-gray-900 mb-2">{{ key }}</div>
              <div class="text-xs text-gray-600">
                <pre class="whitespace-pre-wrap">{{ JSON.stringify(value, null, 2) }}</pre>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Summary Information -->
      <div class="bg-gray-50 rounded-lg p-4">
        <h5 class="font-medium text-gray-800 mb-3">Match-up Summary</h5>
        <div class="text-sm text-gray-600">
          <p><span class="font-medium">Data Type:</span> {{ Array.isArray(opponentMatchUps) ? 'Array' : 'Object' }}</p>
          <p><span class="font-medium">Total Items:</span> {{ Array.isArray(opponentMatchUps) ? opponentMatchUps.length : Object.keys(opponentMatchUps).length }}</p>
          <p><span class="font-medium">League ID:</span> {{ leagueId }}</p>
          <p><span class="font-medium">Team ID:</span> {{ teamId }}</p>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center py-8">
      <i class="fas fa-info-circle text-gray-400 text-4xl mb-4"></i>
      <p class="text-gray-600">No opponent match-ups data available</p>
    </div>
  </div>
</template>

<style scoped>
/* Additional custom styles if needed */
</style>



